<template>
  <sidebar-row>
    <v-divider class="mb-6 mt-12"></v-divider>
    <v-subheader class="text-left text-body-1 font-weight-medium font--text pa-0">
      {{ $i18n.t('sidebar.results') }}
    </v-subheader>
    <v-row class="mt-2" no-gutters>
      <v-col class="text-left" cols="auto"> {{ $i18n.t('sidebar.errorAmount') }}</v-col>
      <v-col cols="auto" class="ml-5">
        <v-chip label color="#BF360C" text-color="white" class="pa-2 rounded-lg" small>
          {{ errorCount }}
        </v-chip>
      </v-col>
    </v-row>
    <v-expansion-panels accordion class="mt-2">
      <v-expansion-panel v-for="row in computedErrors" :key="row.errorType">
        <v-expansion-panel-header>
          <v-row no-gutters dense style="align-items: center;">
            <v-col cols="auto" style="font-size: 12px">
              {{ row.description }}
            </v-col>
            <v-col cols="auto" class="pl-5">
              <v-chip
                label
                :color="row.count === 0 ? 'success' : '#BF360C'"
                text-color="white"
                class="rounded-lg"
                small
              >
                {{ row.count }}
              </v-chip>
            </v-col>
          </v-row>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-data-table
            dense
            :hide-default-footer="row.count < 50"
            disable-filtering
            disable-sort
            :no-data-text="$i18n.t('sidebar.modelingCheck.allFixed')"
            :headers="row.tableHeaders"
            item-key="name"
            item-class="tableItem"
            :items="row.errors"
            style="max-height: 200px; overflow-y: auto"
            :footer-props="{
              itemsPerPageOptions: [20, 50, 100],
            }"
          >
            <template #item.actions="{ item }">
              <dots-menu
                v-if="row.multipleChoice"
                :items="computedFixItems(item)"
                style="width: fit-content"
                exact-items
                is-absolute
                @selectedItem="
                  repairItem({
                    index: row.errors.indexOf(item),
                    row: row,
                    dataSource: item.datasource,
                    id: item.id,
                    selectedValue: $event,
                  })
                "
              >
                <template #activator="{on, attrs}">
                  <v-tooltip bottom color="font">
                    <template #activator="{ on: onTooltip }">
                      <span v-on="{ ...onTooltip }">
                        <v-icon
                          small
                          v-on="{ ...on }"
                          v-bind="attrs"
                          :disabled="!row.fixable"
                          class="pr-2"
                          color="black"
                          style="font-size: 16px"
                        >
                          mdi-wrench
                        </v-icon>
                      </span>
                    </template>
                    {{ row.tooltipText }}
                  </v-tooltip>
                </template>
              </dots-menu>
              <v-tooltip bottom color="font" v-else>
                <template #activator="{ on: onTooltip }">
                  <v-icon
                    small
                    class="mr-2"
                    v-on="{ ...onTooltip }"
                    @click="
                      repairItem({
                        index: row.errors.indexOf(item),
                        row: row,
                        dataSource: item.datasource,
                        id: item.id,
                      })
                    "
                    color="black"
                    style="font-size: 16px"
                  >
                    mdi-wrench
                  </v-icon>
                </template>
                {{ row.tooltipText }}
              </v-tooltip>
              <v-tooltip bottom color="font" v-if="item.id || item.id === 0">
                <template #activator="{ on: onTooltip }">
                  <v-icon
                    small
                    class="mr-2"
                    v-on="{ ...onTooltip }"
                    @click="zoomToItem(item)"
                    color="black"
                    style="font-size: 18px"
                  >
                    mdi-magnify
                  </v-icon>
                </template>
                {{ $i18n.t('sidebar.modelingCheck.zoomToObject') }}
              </v-tooltip>
            </template>
            <template #header.columnName="{ header }">
              <v-tooltip bottom color="font">
                <template #activator="{ on }">
                  {{ header.text }}
                  <v-icon v-on="on" style="font-size: 12px">
                    mdi-help-circle
                  </v-icon>
                </template>
                {{ $i18n.t('sidebar.columnNameTooltip') }}
              </v-tooltip>
            </template>
            <template #header.pointIds="{ header }">
              <v-tooltip bottom color="font">
                <template #activator="{ on }">
                  {{ header.text }}
                  <v-icon v-on="on" style="font-size: 12px">
                    mdi-help-circle
                  </v-icon>
                </template>
                {{ $i18n.t('sidebar.pointsIdsTooltip') }}
              </v-tooltip>
            </template>
          </v-data-table>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
  </sidebar-row>
</template>

<script>
import SidebarRow from '@/components/SidebarRow';
import DotsMenu from '@/components/DotsMenu';
import { get } from 'vuex-pathify';

export default {
  name: 'ModelingNetworkErrors',
  components: { DotsMenu, SidebarRow },
  props: {
    errors: {
      type: Array,
      default: () => [],
      required: true,
    },
    errorCount: {
      type: Number,
      default: 0,
    },
  },
  data: () => ({}),
  computed: {
    computedErrors() {
      return this.errors.map(value => {
        return {
          ...value,
          errors: value.errors.map(v => {
            return {
              ...v,
              id: this.getDuplicateId(v),
              columnName: this.getErrorColumnNames(v),
              pointIds: this.getDuplicatePoints(v),
            };
          }),
        };
      });
    },
    projectElements: get('layers/project@layers'),
    projectLayers() {
      return this.$_getFlatGroupsLayers(this.projectElements).layers;
    },
  },
  methods: {
    getDuplicatePoints(value) {
      if (!value.id && value.id !== 0) {
        return value.fix?.join(', ');
      } else {
        return null;
      }
    },
    getErrorColumnNames(value) {
      if (!value.fields) {
        return null;
      } else {
        return value.fields.join(', ');
      }
    },
    getDuplicateId(value) {
      if (!value.id && value.id !== 0) {
        return value.fix?.[0];
      } else {
        return value.id;
      }
    },
    computedFixItems(item) {
      if (item.fix) {
        return item.fix.map(id => {
          return {
            name: id,
          };
        });
      } else {
        return [];
      }
    },
    zoomToItem(item) {
      const { datasource, id } = item;
      const layer = this.projectLayers.find(layer => layer.data_source_name === datasource);
      if (!layer) {
        this.$store.set('snackbar/PUSH_MESSAGE!', {
          message: this.$i18n.t('dialog.layerIsNotFoundForDataSource'),
        });
        return;
      }
      if (!layer.visible) {
        this.$store.set('snackbar/PUSH_MESSAGE!', {
          message: this.$i18n.t('dialog.layerIsNotVisible'),
        });
        return;
      }

      this.$root.$emit('zoomToDatasourceFeature', datasource, id);
    },
    repairItem(item) {
      const { index, row, selectedValue, dataSource, id, multiple } = item;
      this.$emit('fixProblem', {
        index: index,
        errorType: row.errorType,
        selected: selectedValue || null,
        dataSource: dataSource,
        multiple: multiple,
        id: id,
      });
    },
  },
};
</script>

<style scoped lang="scss">
::v-deep {
  .v-data-table > .v-data-table__wrapper > table > tbody > tr > td {
    padding: 0 6px;
  }
  .v-data-table > .v-data-table__wrapper > table > thead > tr > th {
    padding: 0 6px;
  }
}
</style>
